import InfoCard from "@components/molecules/InfoCard";
import CircularButtonWithIcon from "@components/molecules/CircularButtonWithIcon";
import { FaPersonRunning, FaCalendarDays, FaHeartPulse, FaCalendarPlus, FaGear, FaCrown } from "react-icons/fa6";
import { HttpError, useGo, useList, useTranslate, Link, useGetIdentity, CanAccess } from "@refinedev/core";
import { calendar_v3 } from "googleapis"
import { useMemo } from "react";
import dayjs from "dayjs";
import { useAppCalendar } from "hooks/calendar/useAppCalendar";
import { ContentCard } from "@components/organisms/ContentCard";
import { Article } from "types/articles";
import { EventCode, searchMetadataString } from "core/calendar/definitions";
import { AppointmentEvent } from "@components/atoms/AppointmentEvent";
import { User_User_Public } from "@graphql/schema.types";
import { useList as useListRefine } from "@refinedev/core";
import { FaSearch } from "react-icons/fa";
import { BillingInfoForm } from "@components/organisms/BillingInfoForm";
import { useBillingInfo } from "hooks/useBillingInfo";

import { WellnessBanner } from "@components/organisms/WellnessBanner";
import { motion } from "framer-motion";

const containerVariants = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

export const HomeUser = () => {
    const translate = useTranslate()
    const go = useGo()
    const { data: identity } = useGetIdentity<{ name?: string; display_name?: string }>();
    const userName = identity?.display_name?.split(' ')?.[0] || identity?.name?.split(" ")?.[0] || "";
    const { hasBillingInfo, isLoading: isLoadingBillingInfo, refetch: refetchBillingInfo } = useBillingInfo();

    /*
    * Appointments
    */
    const calendarId = useAppCalendar()

    const date = useMemo(() => dayjs(), [])
    const {
        data: events,
        isLoading: isLoadingAppoiments,
    } = useList<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event>({
        dataProviderName: 'gapi',
        resource: `calendar/calendars/:calendar/events`,
        meta: {
            params: {
                calendar: calendarId?.appointments
            }
        },
        queryOptions: {
            enabled: !!calendarId,
        },
        sorters: [
            {
                field: 'startTime',
                order: 'asc'
            }
        ],
        pagination: {
            pageSize: 1,
        },
        filters: [
            {
                field: 'timeMin',
                operator: 'eq',
                value: date.toISOString()
            },
            {
                field: 'showDeleted',
                operator: 'eq',
                value: false
            },
            {
                field: 'singleEvents',
                operator: 'eq',
                value: true
            },
            {
                field: 'q',
                operator: 'eq',
                value: searchMetadataString({ code: EventCode.APPOINTMENT })
            }
        ]
    })
    const followingEvent = events?.data[0]
    const followingDate = followingEvent ? dayjs(followingEvent.start?.dateTime) : null

    // Get user email from attendees (not a service account)
    const userEmail = followingEvent?.attendees?.find(a => a?.email && !a.email.includes('gserviceaccount'))?.email
    // Fetch user public info for contact card
    const { data: userContactData } = useListRefine<User_User_Public, HttpError, User_User_Public>({
        resource: 'user_user_public',
        filters: [
            {
                field: 'email',
                operator: 'eq',
                value: userEmail,
            },
        ],
        queryOptions: {
            enabled: !!userEmail,
        },
        meta: {
            fields: [
                'email',
                'avatar_url',
                'display_name',
            ],
        },
    })
    const userContact = userContactData?.data?.[0]

    const {
        data,
        isLoading: isLoadingArticles,
    } = useList<Article, HttpError>({
        resource: 'articles',
        sorters: [
            {
                field: 'published_at',
                order: 'desc'
            }
        ],
        pagination: {
            pageSize: 5,
        },
        meta: {
            fields: ['id', 'title', 'content', 'author_id', 'landing_image', 'tags', 'published_at', 'created_at', 'updated_at']
        }
    })
    const posts = data?.data

    // Mostrar loading mientras verifica si ya tiene registro de facturación
    if (isLoadingBillingInfo) {
        return (
            <div className="flex flex-col min-h-screen w-full">
                <header className=" md:h-auto md:ml-48 relative">
                    <div className="hidden md:block z-30 absolute top-0 -ml-20 left-0 w-20 h-8 bg-transparent rounded-tr-full shadow-[2rem_0_var(--color-secondary)]"></div>
                    <div className="w-full bg-secondary pb-22 md:pb-8 md:pl-12 pt-6 px-4 rounded-bl-[4rem] md:rounded-bl-4xl relative overflow-hidden">
                        <img src="/img/hachi-corner-text-light.png" alt="hachimind logo" className="lg:hidden w-40 absolute bottom-0 right-0 delay-500 animate-appear-from-bottom" />
                        <div className="flex items-center justify-between mb-6">
                            <h1 className="text-2xl font-bold text-base-100">
                                {userName
                                    ? translate("page.home.greeting", { name: userName })
                                    : translate("page.home.greeting.noName")}
                            </h1>
                        </div>
                    </div>
                </header>
                <main className="flex-1 px-4 py-6 flex items-center justify-center">
                    <div className="loading loading-spinner loading-lg"></div>
                </main>
            </div>
        );
    }

    // FOrmulario si no hay registro en la tabla de facturación
    if (!hasBillingInfo) {
        return (
            <div className="flex flex-col min-h-screen w-full">
                <header className=" md:h-auto md:ml-48 relative">
                    <div className="hidden md:block z-30 absolute top-0 -ml-20 left-0 w-20 h-8 bg-transparent rounded-tr-full shadow-[2rem_0_var(--color-secondary)]"></div>
                    <div className="w-full bg-secondary pb-22 md:pb-8 md:pl-12 pt-6 px-4 rounded-bl-[4rem] md:rounded-bl-4xl relative overflow-hidden">
                        <img src="/img/hachi-corner-text-light.png" alt="hachimind logo" className="lg:hidden w-40 absolute bottom-0 right-0 delay-500 animate-appear-from-bottom" />
                        <div className="flex items-center justify-between mb-6">
                            <h1 className="text-2xl font-bold text-base-100">
                                {userName
                                    ? translate("page.home.greeting", { name: userName })
                                    : translate("page.home.greeting.noName")}
                            </h1>
                        </div>
                    </div>
                </header>
                <main className="flex-1 px-4 py-6">
                    <BillingInfoForm onSuccess={refetchBillingInfo} />
                </main>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 w-full min-h-screen">
            {/* Top Header Section */}
            <header className="md:h-auto md:ml-24 relative z-10">
                <div className="hidden md:block z-30 absolute top-0 -ml-20 left-0 w-20 h-8 bg-transparent rounded-tr-full shadow-[2rem_0_var(--color-secondary)]"></div>
                <div className="w-full bg-secondary pb-24 md:pb-12 md:pl-12 pt-8 px-6 rounded-bl-[3rem] md:rounded-bl-[4rem] relative overflow-hidden shadow-xl h-48">
                    {/* Decorative Background Elements */}
                    <div className="absolute inset-0 from-white/10 via-transparent to-black/10 pointer-events-none" />
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
                    <img src="/img/hachi-corner-text-light.png" alt="hachimind logo" className="lg:hidden w-48 absolute bottom-0 right-0 delay-500 animate-appear-from-bottom opacity-100 mix-blend-overlay" />

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
                        <div className="flex flex-col">
                            <span className="text-secondary-content/70 text-sm font-semibold mb-1 tracking-widest uppercase">{translate("page.home.welcome", "Bienvenido")}</span>
                            <h1 className="text-3xl sm:text-4xl font-extrabold text-base-100 tracking-tight drop-shadow-sm">
                                {userName
                                    ? translate("page.home.greeting", { name: userName })
                                    : translate("page.home.greeting.noName")}
                            </h1>
                        </div>

                        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                            <Link
                                to="/plans"
                                className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-500 px-5 py-2.5 sm:px-7 sm:py-3 shadow-[0_4px_20px_rgba(251,191,36,0.4)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_4px_30px_rgba(251,191,36,0.6)] border border-yellow-200/50"
                            >
                                <div className="absolute inset-0 flex h-full w-full justify-center [transform:skew(-12deg)_translateX(-150%)] group-hover:duration-1000 group-hover:[transform:skew(-12deg)_translateX(150%)]">
                                    <div className="w-12 h-full bg-white/50 blur-sm" />
                                </div>
                                <FaCrown className="text-amber-900 text-lg sm:text-xl relative z-10 drop-shadow-sm" />
                                <span className="text-amber-900 font-bold text-sm sm:text-base tracking-wide relative z-10 hidden sm:inline-block">
                                    {translate("page.plans.title", "Ver Planes")}
                                </span>
                            </Link>
                            <button className="p-3 sm:p-3.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md transition-all duration-300 border border-white/10 hover:scale-105 shadow-sm">
                                <FaSearch className="w-5 h-5 text-base-100" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1 px-4 sm:px-6 md:px-8 pb-12 mt-8 md:-mt-8 relative z-20 w-full max-w-[1600px] mx-auto md:w-[calc(100%-12rem)]">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 w-full"
                >
                    {/* Quick Actions Bento Box */}
                    <motion.div variants={itemVariants} className="col-span-1 md:col-span-2 lg:col-span-12">
                        <div className="bg-base-100/90 backdrop-blur-xl rounded-[2rem] p-6 sm:p-8 shadow-sm border border-base-200/60 shadow-inner">
                            <nav className="flex flex-wrap gap-4 sm:gap-6 justify-center sm:justify-start" aria-label="Main quick actions">
                                <Link to="/helps" className="no-underline group">
                                    <div className="transition-transform duration-300 group-hover:-translate-y-1">
                                        <CircularButtonWithIcon
                                            data-tip={translate("page.home.topButtons.help.tip")}
                                            text={translate("page.home.topButtons.help.title")}
                                            icon={FaHeartPulse}
                                            className="sm:w-auto hover:bg-transparent" />
                                    </div>
                                </Link>
                                <div className="group cursor-pointer">
                                    <div className="transition-transform duration-300 group-hover:-translate-y-1">
                                        <CircularButtonWithIcon
                                            data-tip={translate("page.home.topButtons.assistance.tip")}
                                            text={translate("page.home.topButtons.assistance.title")}
                                            icon={FaPersonRunning}
                                            className="sm:w-auto hover:bg-transparent" />
                                    </div>
                                </div>
                                <Link
                                    className="no-underline group"
                                    go={{
                                        to: { resource: "appointment", action: "create" },
                                    }}>
                                    <div className="transition-transform duration-300 group-hover:-translate-y-1">
                                        <CircularButtonWithIcon
                                            data-tip={translate("page.home.topButtons.schedule.tip")}
                                            text={translate("page.home.topButtons.schedule.title")}
                                            icon={FaCalendarPlus}
                                            className="sm:w-auto hover:bg-transparent" />
                                    </div>
                                </Link>
                                <Link
                                    className="no-underline group"
                                    go={{
                                        to: { resource: "config", action: "list" },
                                    }}>
                                    <div className="transition-transform duration-300 group-hover:-translate-y-1">
                                        <CircularButtonWithIcon
                                            data-tip={translate("page.home.topButtons.config.tip")}
                                            text={translate("page.home.topButtons.config.title")}
                                            icon={FaGear}
                                            className="sm:w-auto hover:bg-transparent" />
                                    </div>
                                </Link>
                            </nav>
                        </div>
                    </motion.div>

                    {/* Next Appointment Bento Box (Spans 4 columns on large screens) */}
                    <motion.div variants={itemVariants} className="col-span-1 md:col-span-2 lg:col-span-5 xl:col-span-4 flex flex-col">
                        <section className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-[2rem] p-6 sm:p-8 shadow-sm border border-base-200/60 h-full flex flex-col relative overflow-hidden group" aria-labelledby="next-appointment-heading">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl -mr-16 -mt-16 transition-transform duration-700 group-hover:scale-150 group-hover:bg-primary/10"></div>
                            <h2 id="next-appointment-heading" className="text-xl font-bold text-base-content/90 mb-6 relative z-10">{translate("page.home.new-appointment")}</h2>

                            <div className="dropdown w-full relative z-10 flex-1 flex flex-col">
                                <div tabIndex={0} role="button" className="w-full flex-1">
                                    <InfoCard
                                        loading={isLoadingAppoiments}
                                        title={translate('page.home.next-appointment')}
                                        description={!isLoadingAppoiments ? (followingDate ? (
                                            <div className="flex items-center flex-wrap gap-2 mt-1">
                                                <span className="capitalize font-medium">{`${followingDate.format('dddd, HH:mm')}`}</span>
                                                <span className="badge badge-primary border-none shadow-sm">{`${date.to(followingDate)}`}</span>
                                            </div>
                                        ) : translate('page.home.no-following-appointment')) : '-'}
                                        icon={FaCalendarDays}
                                    />
                                </div>
                                {followingEvent && (
                                    <AppointmentEvent
                                        tabIndex={0}
                                        className="dropdown-content z-20 mt-2 w-full sm:w-auto rounded-2xl shadow-xl border border-base-200 overflow-hidden"
                                        event={followingEvent}
                                        contact={userContact}
                                        name="user"
                                    />
                                )}
                            </div>
                        </section>
                    </motion.div>

                    {/* Wellness Banner Bento Box (Spans 8 columns on large screens) */}
                    <motion.div variants={itemVariants} className="col-span-1 md:col-span-2 lg:col-span-7 xl:col-span-8 flex flex-col h-full min-h-[300px]">
                        <WellnessBanner />
                    </motion.div>

                    {/* News / Knowledge Bento Box (Full width) */}
                    <motion.div variants={itemVariants} className="col-span-1 md:col-span-2 lg:col-span-12">
                        <section className="bg-base-100/95 backdrop-blur-2xl rounded-[2rem] p-6 sm:p-8 shadow-sm border border-base-200/60" aria-labelledby="news-knowledge-heading">
                            <div className="flex items-center justify-between mb-8">
                                <h2 id="news-knowledge-heading" className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary drop-shadow-sm">
                                    {`${translate("page.home.news")} / ${translate("page.home.knowledge")}`}
                                </h2>
                            </div>

                            <div className="carousel carousel-center w-full gap-4 pb-4">
                                {isLoadingArticles ? (
                                    Array.from({ length: 4 }).map((_, i) => (
                                        <div key={i} className="carousel-item w-[85%] md:w-[45%] lg:w-[30%] xl:w-[24%]">
                                            <div className="h-[280px] w-full bg-base-200/50 rounded-3xl skeleton flex flex-col justify-end p-6 border border-base-200">
                                                <div className="w-2/3 h-6 bg-base-300 rounded mb-2"></div>
                                                <div className="w-1/2 h-4 bg-base-300 rounded"></div>
                                            </div>
                                        </div>
                                    ))
                                ) : (!posts || posts.length === 0) ? (
                                    <div className="w-full py-12 flex flex-col items-center justify-center text-center bg-base-200/20 rounded-3xl border border-dashed border-base-300 mx-2">
                                        <div className="text-4xl mb-3 opacity-30">📚</div>
                                        <h3 className="font-bold opacity-70">{translate("articles.home.empty", "Próximamente más contenido")}</h3>
                                        <p className="text-xs opacity-50 mt-1">{translate("articles.home.empty_desc", "Estamos preparando nuevos artículos para ti.")}</p>
                                    </div>
                                ) : (
                                    posts.map(post => (
                                        <div key={post.id!} className="carousel-item w-[85%] md:w-[45%] lg:w-[30%] xl:w-[24%] overflow-visible">
                                            <div className="w-full h-full transition-all duration-300 hover:-translate-y-2 hover:shadow-xl rounded-2xl group">
                                                <ContentCard post={post} />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </section>
                    </motion.div>
                </motion.div>
            </main>
        </div>
    )
}
